import { PORTFOLIO } from "@/content/portfolio";
import { CountUp, Section } from "./primitives";

/**
 * Trust strip. Every value is read from the verified portfolio dataset —
 * nothing here is authored in this component.
 */
type Proof = {
  label: string;
  client: string;
  source: string;
  /** Numeric parts for the count-up; the suffix carries the unit. */
  n: number;
  prefix?: string;
  suffix: string;
  decimals?: number;
};

function metric(slug: string, label: string) {
  const project = PORTFOLIO.find((p) => p.slug === slug);
  const m = project?.metrics.find((x) => x.label === label);
  return { project, m };
}

function buildProofs(): Proof[] {
  const out: Proof[] = [];

  const visits = metric("foreign-affairs-auto", "Organic visits");
  if (visits.m && visits.project) {
    out.push({
      label: "Monthly organic visits",
      client: visits.project.client,
      source: `${visits.m.source ?? ""} · ${visits.m.captured ?? ""}`.trim(),
      n: 6.1,
      suffix: "K",
      decimals: 1,
    });
  }

  const impressions = metric("lynx-systems", "GSC Impressions");
  if (impressions.m && impressions.project) {
    out.push({
      label: "Search impressions in 6 months",
      client: impressions.project.client,
      source: `${impressions.m.source ?? ""} · ${impressions.m.captured ?? ""}`.trim(),
      n: 619,
      suffix: "K",
    });
  }

  const clicks = metric("air-tech-pro", "Organic Clicks");
  if (clicks.m && clicks.project) {
    out.push({
      label: "Organic click growth",
      client: clicks.project.client,
      source: `${clicks.m.source ?? ""} · ${clicks.m.captured ?? ""}`.trim(),
      n: 78,
      prefix: "+",
      suffix: "%",
    });
  }

  const cited = metric("carage-auto", "AI Cited Pages");
  if (cited.m && cited.project) {
    out.push({
      label: "Pages cited in AI answers",
      client: cited.project.client,
      source: `${cited.m.source ?? ""} · ${cited.m.captured ?? ""}`.trim(),
      n: 137,
      suffix: "",
    });
  }

  return out;
}

export function HomeProofV2() {
  const proofs = buildProofs();
  if (proofs.length === 0) return null;

  return (
    <Section tone="raised" labelledBy="proof-heading">
      <div className="container-page py-12 md:py-16">
        <h2 id="proof-heading" className="sr-only">
          Verified client results
        </h2>
        <dl className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
          {proofs.map((p) => (
            <div key={p.label} className="border-l border-border pl-5">
              <dd className="font-display text-4xl leading-none md:text-5xl">
                <CountUp
                  value={p.n}
                  prefix={p.prefix}
                  suffix={p.suffix}
                  decimals={p.decimals ?? 0}
                />
              </dd>
              <dt className="mt-3 text-sm leading-relaxed text-foreground/90">
                {p.label}
              </dt>
              <p className="mt-2 text-[0.7rem] uppercase tracking-[0.18em] text-muted-foreground">
                {p.client} · {p.source}
              </p>
            </div>
          ))}
        </dl>
      </div>
    </Section>
  );
}
